import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaUsers,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClipboardCheck,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaStickyNote,
  FaMapSigns,
  FaTrain,
  FaHotel,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  getAllActorsByEventId,
  toggleActeurValidation,
  getAllResults,
} from "./../../../services/acteureventServices";
import { getAllEvents } from "./../../../services/eventServices";
import NoteModal from "./NoteModal";
import Button from "../../../components/Button/Button";
import useScrollToTop from "./../../../hooks/useScollToTop";
import ScrollIndicator from "./../../Home/ScrollIndicator";
import InfoBubble from "./../../../components/InfoBubble/InfoBubble";

/* ================= Helpers ================= */

const ROLE_SECTIONS = [
  {
    key: "Organisateur",
    label: "Organisateurs",
    description: "Direction générale de la manifestation.",
    tone: "border-amber-200 bg-amber-50/80 dark:border-amber-500/20 dark:bg-amber-500/10",
    badge:
      "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  },
  {
    key: "ResponsableLigue",
    label: "Responsables Ligue",
    description: "Référents Ligue, coordination globale.",
    tone: "border-sky-200 bg-sky-50/80 dark:border-sky-500/20 dark:bg-sky-500/10",
    badge: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  },
  {
    key: "ResponsableComite",
    label: "Responsables Comité",
    description: "Référents Comité, coordination globale.",
    tone: "border-cyan-200 bg-cyan-50/80 dark:border-cyan-500/20 dark:bg-cyan-500/10",
    badge: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-300",
  },
  {
    key: "Benevolontaire",
    label: "Bénévolontaires",
    description: "Accueil, logistique, buvette, etc.",
    tone: "border-emerald-200 bg-emerald-50/80 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    badge:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  },
  {
    key: "ResponsableArbitrage",
    label: "Responsables Arbitrage",
    description: "Pilotage de l’arbitrage sur la journée.",
    tone: "border-indigo-200 bg-indigo-50/80 dark:border-indigo-500/20 dark:bg-indigo-500/10",
    badge:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
  },
  {
    key: "Notateur",
    label: "Notateurs",
    description: "Gestion des feuilles, informatique, tables.",
    tone: "border-violet-200 bg-violet-50/80 dark:border-violet-500/20 dark:bg-violet-500/10",
    badge:
      "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  },
  {
    key: "Arbitre",
    label: "Arbitres",
    description: "Arbitrage sur les surfaces.",
    tone: "border-orange-200 bg-orange-50/80 dark:border-orange-500/20 dark:bg-orange-500/10",
    badge:
      "bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300",
  },
  {
    key: "CommissaireSportif",
    label: "Commissaires Sportifs",
    description: "Gestion des tables, annonces, appels.",
    tone: "border-rose-200 bg-rose-50/80 dark:border-rose-500/20 dark:bg-rose-500/10",
    badge: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300",
  },
  {
    key: "Juge",
    label: "Juges",
    description: "Juges techniques (kata, etc.).",
    tone: "border-slate-200 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-800/60",
    badge: "bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
  },
];

const createEmptyActorsState = () =>
  ROLE_SECTIONS.reduce((acc, section) => {
    acc[section.key] = [];
    return acc;
  }, {});

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateRange = (start, end) => {
  if (!start) return "-";
  const dStart = formatDate(start);
  if (!end || end === "0000-00-00") return dStart;
  const dEnd = formatDate(end);
  return `${dStart}  →  ${dEnd}`;
};

const getConvocationVisual = (status) => {
  switch (status) {
    case "accepted":
      return { icon: "✅", label: "Convocation acceptée" };
    case "rejected":
      return { icon: "❌", label: "Convocation rejetée" };
    default:
      return { icon: "⏹️", label: "Convocation en attente" };
  }
};

const getPresenceVisual = (isValidate) => {
  return isValidate
    ? { icon: "✅", label: "Présent le jour de l'événement" }
    : { icon: "❌", label: "Absent le jour de l'événement" };
};

const getRoleKeyFromPoste = (poste) => {
  switch (poste) {
    case "Organisateur":
      return "Organisateur";
    case "Responsable Ligue":
      return "ResponsableLigue";
    case "Responsable Comité":
      return "ResponsableComite";
    case "Bénévolontaire":
      return "Benevolontaire";
    case "Responsable Arbitrage":
      return "ResponsableArbitrage";
    case "Notateur":
      return "Notateur";
    case "Juge":
      return "Juge";
    case "Arbitre":
      return "Arbitre";
    case "Commissaire Sportif":
      return "CommissaireSportif";
    default:
      return null;
  }
};

const getActorPivot = (actor) =>
  actor?.ActeurEvent || actor?.ActeursEvent || actor?.ActeursEvents || {};

const normalizeText = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const formatTapisValue = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return "";
  if (/^\d+$/.test(cleaned)) return `Tapis ${cleaned}`;
  return cleaned;
};

const getTapisSortValue = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return Number.MAX_SAFE_INTEGER;

  const match = cleaned.match(/(\d+)/);
  if (match) return parseInt(match[1], 10);

  return Number.MAX_SAFE_INTEGER - 1;
};

const sortActorsForRole = (list = [], roleKey) => {
  return [...list].sort((a, b) => {
    const pivotA = getActorPivot(a);
    const pivotB = getActorPivot(b);

    if (roleKey === "Arbitre" || roleKey === "CommissaireSportif") {
      const tapisA = getTapisSortValue(pivotA.tapis);
      const tapisB = getTapisSortValue(pivotB.tapis);

      if (tapisA !== tapisB) return tapisA - tapisB;

      const tapisLabelA = normalizeText(formatTapisValue(pivotA.tapis));
      const tapisLabelB = normalizeText(formatTapisValue(pivotB.tapis));

      if (tapisLabelA !== tapisLabelB) {
        return tapisLabelA.localeCompare(tapisLabelB, "fr");
      }
    }

    return `${a.nom} ${a.prenom}`
      .toLowerCase()
      .localeCompare(`${b.nom} ${b.prenom}`.toLowerCase(), "fr");
  });
};

const buildConvocationCountMap = (rows = []) => {
  return rows.reduce((acc, row) => {
    const eventId = row?.event_id ?? row?.Event?.id;
    if (!eventId) return acc;
    acc[eventId] = (acc[eventId] || 0) + 1;
    return acc;
  }, {});
};

const getSupportStatusLabel = (status) => {
  switch (status) {
    case "approved":
      return "Validée";
    case "rejected":
      return "Refusée";
    case "pending":
      return "En attente";
    default:
      return "—";
  }
};

const getSupportStatusTone = (status) => {
  switch (status) {
    case "approved":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
    case "rejected":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";
    default:
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
  }
};

const getSimpleTapisLabel = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return "";

  const match = cleaned.match(/(\d+)/);
  if (match) return `T${match[1]}`;

  return cleaned;
};

const hasTransportRequest = (acteurEvent) =>
  acteurEvent?.need_transport_support === true;

const hasAccommodationRequest = (acteurEvent) =>
  acteurEvent?.need_accommodation_support === true;

/* ================= Small UI parts ================= */

function StatChip({ icon, label, value, tone = "slate" }) {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    rose: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    slate:
      "border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-semibold ${tones[tone]}`}
    >
      {icon}
      <span>
        {label} : {value}
      </span>
    </span>
  );
}

function ActorRow({ actor, eventId, onToggleValidation, onOpenNote }) {
  const acteurEvent = getActorPivot(actor);
  const convocationVisual = getConvocationVisual(
    acteurEvent.response_status || "pending",
  );
  const presenceVisual = getPresenceVisual(acteurEvent.is_validate);

  const tapisShort = getSimpleTapisLabel(acteurEvent.tapis);

  const transportTone = getSupportStatusTone(
    acteurEvent.transport_support_status,
  );
  const accommodationTone = getSupportStatusTone(
    acteurEvent.accommodation_support_status,
  );

  return (
    <li
      key={`${actor.id}-${acteurEvent.event_id || eventId}`}
      className="rounded-2xl border border-white/70 bg-white/90 px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-900/80"
    >
      <div className="flex items-center gap-2 min-w-0 overflow-x-auto whitespace-nowrap scrollbar-thin">
        <span
          className="shrink-0 text-sm"
          title={convocationVisual.label}
          aria-label={convocationVisual.label}
        >
          {convocationVisual.icon}
        </span>

        <button
          type="button"
          onClick={() => onToggleValidation(acteurEvent.acteur_id)}
          className="shrink-0 text-sm transition-transform hover:scale-110"
          title={`${presenceVisual.label} — cliquer pour ${
            acteurEvent.is_validate ? "marquer absent" : "marquer présent"
          }`}
          aria-label={presenceVisual.label}
        >
          {presenceVisual.icon}
        </button>

        <button
          type="button"
          onClick={() =>
            onOpenNote({
              ...acteurEvent,
              acteurLabel: `${actor.nom?.toUpperCase()} ${actor.prenom}`,
            })
          }
          className="shrink min-w-0 truncate text-left text-sm font-semibold text-slate-900 hover:underline dark:text-white"
          title={`${actor.nom?.toUpperCase()} ${actor.prenom}`}
        >
          {actor.nom?.toUpperCase()} {actor.prenom}
        </button>

        {tapisShort && (
          <span
            className="shrink-0 inline-flex items-center justify-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700"
            title={formatTapisValue(acteurEvent.tapis)}
          >
            {tapisShort}
          </span>
        )}

        {hasTransportRequest(acteurEvent) && (
          <span
            className={`shrink-0 inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${transportTone}`}
            title={`Transport : ${getSupportStatusLabel(
              acteurEvent.transport_support_status,
            )}`}
          >
            <FaTrain />
          </span>
        )}

        {hasAccommodationRequest(acteurEvent) && (
          <span
            className={`shrink-0 inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${accommodationTone}`}
            title={`Hébergement : ${getSupportStatusLabel(
              acteurEvent.accommodation_support_status,
            )}`}
          >
            <FaHotel />
          </span>
        )}

        {acteurEvent?.note && (
          <span
            className="shrink-0 inline-flex items-center justify-center rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
            title="Note présente"
          >
            <FaStickyNote />
          </span>
        )}
      </div>
    </li>
  );
}

/* ================= Page ================= */

const ConvocationByEventPage = () => {
  useScrollToTop();
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [actors, setActors] = useState(createEmptyActorsState());
  const [selectedActorEvent, setSelectedActorEvent] = useState(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);

  const [navigableEvents, setNavigableEvents] = useState([]);
  const [loadingActors, setLoadingActors] = useState(false);
  const [loadingNavigation, setLoadingNavigation] = useState(false);

  const fetchActors = useCallback(async () => {
    if (!eventId) return;

    try {
      setLoadingActors(true);

      const data = await getAllActorsByEventId(eventId);
      const sortedActors = createEmptyActorsState();

      if (data?.Acteur && Array.isArray(data.Acteur)) {
        data.Acteur.forEach((actor) => {
          const pivot = getActorPivot(actor);
          const roleKey = getRoleKeyFromPoste(pivot.poste);
          if (roleKey && sortedActors[roleKey]) {
            sortedActors[roleKey].push(actor);
          }
        });
      }

      Object.keys(sortedActors).forEach((roleKey) => {
        sortedActors[roleKey] = sortActorsForRole(
          sortedActors[roleKey],
          roleKey,
        );
      });

      setEvent(data || null);
      setActors(sortedActors);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des acteurs de l'événement",
        error,
      );
      toast.error("Erreur lors du chargement des acteurs de l'événement.");
      setActors(createEmptyActorsState());
    } finally {
      setLoadingActors(false);
    }
  }, [eventId]);

  const fetchNavigableEvents = useCallback(async () => {
    try {
      setLoadingNavigation(true);

      const [eventsData, resultsData] = await Promise.all([
        getAllEvents(),
        getAllResults(),
      ]);

      const baseEvents = Array.isArray(eventsData) ? eventsData : [];
      const convocationCountMap = buildConvocationCountMap(
        Array.isArray(resultsData) ? resultsData : [],
      );

      const eventsWithActors = baseEvents
        .map((ev) => ({
          ...ev,
          nombreConvocations: convocationCountMap[ev.id] || 0,
        }))
        .filter((ev) => ev.nombreConvocations > 0)
        .sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
        );

      setNavigableEvents(eventsWithActors);
    } catch (error) {
      console.error(
        "Erreur lors du chargement de la navigation des événements",
        error,
      );
      toast.error("Erreur lors du chargement des événements.");
      setNavigableEvents([]);
    } finally {
      setLoadingNavigation(false);
    }
  }, []);

  useEffect(() => {
    if (eventId) {
      fetchActors();
      fetchNavigableEvents();
    }
  }, [eventId, fetchActors, fetchNavigableEvents]);

  const openNoteModal = (actorEvent) => {
    setSelectedActorEvent(actorEvent);
    setIsNoteModalOpen(true);
  };

  const closeNoteModal = () => {
    setSelectedActorEvent(null);
    setIsNoteModalOpen(false);
  };

  const handleToggleValidation = async (acteurId) => {
    try {
      await toggleActeurValidation(acteurId, eventId);
      toast.success("Présence mise à jour avec succès.");
      await fetchActors();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la validation", error);
      toast.error("Erreur lors de la mise à jour de la validation.");
    }
  };

  const currentIndex = useMemo(
    () => navigableEvents.findIndex((ev) => String(ev.id) === String(eventId)),
    [navigableEvents, eventId],
  );

  const handleNavigate = (direction) => {
    if (!navigableEvents.length || currentIndex === -1) return;

    if (direction === "prev" && currentIndex > 0) {
      navigate(
        `/appli/convocations-admin/${navigableEvents[currentIndex - 1].id}`,
      );
    } else if (
      direction === "next" &&
      currentIndex < navigableEvents.length - 1
    ) {
      navigate(
        `/appli/convocations-admin/${navigableEvents[currentIndex + 1].id}`,
      );
    }
  };

  const allActors = useMemo(() => Object.values(actors).flat(), [actors]);

  const stats = useMemo(() => {
    const total = allActors.length;
    const validated = allActors.filter(
      (a) => getActorPivot(a)?.is_validate,
    ).length;
    const accepted = allActors.filter(
      (a) => getActorPivot(a)?.response_status === "accepted",
    ).length;
    const rejected = allActors.filter(
      (a) => getActorPivot(a)?.response_status === "rejected",
    ).length;
    const pending = allActors.filter((a) => {
      const status = getActorPivot(a)?.response_status;
      return !status || status === "pending";
    }).length;

    return {
      total,
      validated,
      absent: total - validated,
      accepted,
      rejected,
      pending,
    };
  }, [allActors]);

  const hasAnyActors = allActors.length > 0;

  return (
    <div className="w-full pb-10 pt-[96px] font-satoshi dark:text-white">
      <ScrollIndicator />

      <div className="mx-auto max-w-[1400px] px-4">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />
          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>
                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Détails des convocations
                </h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                  Consulte les acteurs convoqués par rôle, suis les réponses aux
                  convocations, les demandes de prise en charge, les tapis et
                  valide leur présence le jour J.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <StatChip
                  icon={<FaUsers />}
                  label="Convoqués"
                  value={stats.total}
                  tone="blue"
                />
                <StatChip
                  icon={<FaCheckCircle />}
                  label="Présents"
                  value={stats.validated}
                  tone="emerald"
                />
                <StatChip
                  icon={<FaTimesCircle />}
                  label="Absents"
                  value={stats.absent}
                  tone="slate"
                />
                <StatChip
                  icon={<FaCheckCircle />}
                  label="Acceptés"
                  value={stats.accepted}
                  tone="emerald"
                />
                <StatChip
                  icon={<FaTimesCircle />}
                  label="Refusés"
                  value={stats.rejected}
                  tone="rose"
                />
                <StatChip
                  icon={<FaHourglassHalf />}
                  label="En attente"
                  value={stats.pending}
                  tone="amber"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5">
          {event ? (
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-sky-500 px-4 py-4 text-white sm:px-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/80">
                      Convocations – {formatDate(event.start)}
                    </p>
                    <h2 className="mt-1 text-xl font-bold sm:text-2xl">
                      {event.titre}
                    </h2>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium">
                        <FaCalendarAlt />
                        {formatDateRange(event.start, event.end)}
                      </span>

                      {event.lieu_event && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium">
                          <FaMapMarkerAlt />
                          {event.lieu_event}
                        </span>
                      )}

                      {event.type_event && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium">
                          <FaClipboardCheck />
                          {event.type_event}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 text-[11px] sm:text-xs lg:max-w-[440px] lg:justify-end">
                    <StatChip
                      icon={<FaUsers />}
                      label="Convoqués"
                      value={stats.total}
                      tone="slate"
                    />
                    <StatChip
                      icon={<FaCheckCircle />}
                      label="Présents"
                      value={stats.validated}
                      tone="emerald"
                    />
                    <StatChip
                      icon={<FaHourglassHalf />}
                      label="En attente"
                      value={stats.pending}
                      tone="amber"
                    />
                  </div>
                </div>
              </div>

              <div className="grid gap-5 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr),280px]">
                <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  {event.description ? (
                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Description
                      </p>
                      <div
                        className="text-sm leading-6"
                        dangerouslySetInnerHTML={{ __html: event.description }}
                      />
                    </div>
                  ) : null}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Type
                      </p>
                      <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                        {event.type_event || "-"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Niveau
                      </p>
                      <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                        {event.level_event || "-"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Catégorie
                      </p>
                      <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                        {event.cate_event || "-"}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/60 dark:ring-slate-700">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Horaires
                      </p>
                      {event.horaire_event ? (
                        <div
                          className="mt-1 font-semibold text-slate-900 dark:text-white"
                          dangerouslySetInnerHTML={{
                            __html: event.horaire_event,
                          }}
                        />
                      ) : (
                        <p className="mt-1 font-semibold text-slate-900 dark:text-white">
                          -
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-full">
                  <div className="overflow-hidden rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700">
                    <img
                      src={event.photo_url || "/img/default_event.jpg"}
                      alt={event.titre}
                      className="h-56 w-full object-cover lg:h-full lg:min-h-[280px]"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[28px] border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              {loadingActors
                ? "Chargement de l’événement..."
                : "Événement introuvable."}
            </div>
          )}
        </div>

        <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
                {loadingNavigation
                  ? "Chargement navigation..."
                  : `${navigableEvents.length} événement(s) avec convocation`}
              </span>

              {currentIndex >= 0 && (
                <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                  Événement {currentIndex + 1} / {navigableEvents.length}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                onClick={() => handleNavigate("prev")}
                text="Événement précédent"
                className="items-center gap-2 btn-sm"
                icon={FaChevronLeft}
                disabled={currentIndex <= 0}
              />

              <Button
                onClick={() => navigate("/appli/convocations-admin")}
                text="Retour à la liste"
                className="items-center gap-2 btn-sm"
              />

              <Button
                onClick={() => handleNavigate("next")}
                text="Événement suivant"
                className="items-center gap-2 btn-sm"
                icon={FaChevronRight}
                disabled={
                  currentIndex < 0 || currentIndex >= navigableEvents.length - 1
                }
              />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <InfoBubble title="Lecture des symboles">
            <p>
              Le premier symbole indique la réponse à la convocation (✅
              acceptée, ❌ rejetée, ⏹️ en attente). Le second indique la
              présence réelle le jour de l’événement (✅ présent, ❌ absent).
              Pour les arbitres et commissaires sportifs, le tapis apparaît
              aussi sous forme de tag et l’ordre d’affichage suit les tapis 1,
              2, 3… Clique sur le nom pour gérer la note, les observations, le
              tapis et les demandes de prise en charge.
            </p>
          </InfoBubble>
        </div>

        <div className="mt-5">
          {loadingActors ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              Chargement des acteurs convoqués...
            </div>
          ) : !hasAnyActors ? (
            <div className="rounded-[28px] border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              Aucun acteur convoqué sur cet événement.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {ROLE_SECTIONS.map((section) => {
                const list = actors[section.key] || [];

                return (
                  <div
                    key={section.key}
                    className={`rounded-[26px] border p-4 shadow-sm ${section.tone}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-slate-800 dark:text-white">
                          {section.label}
                        </h3>
                        <p className="mt-1 text-[11px] text-slate-600 dark:text-slate-300">
                          {section.description}
                        </p>
                      </div>

                      <span
                        className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${section.badge}`}
                      >
                        {list.length}
                      </span>
                    </div>

                    <div className="mt-4">
                      {list.length > 0 ? (
                        <ul className="space-y-2">
                          {list.map((actor) => (
                            <ActorRow
                              key={`${actor.id}-${getActorPivot(actor)?.event_id || eventId}`}
                              actor={actor}
                              eventId={eventId}
                              onToggleValidation={handleToggleValidation}
                              onOpenNote={openNoteModal}
                            />
                          ))}
                        </ul>
                      ) : (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white/70 px-3 py-4 text-center text-xs italic text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
                          Aucun acteur dans ce rôle.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={closeNoteModal}
        actorEvent={selectedActorEvent}
        fetchActors={fetchActors}
      />

      <ToastContainer />
    </div>
  );
};

export default ConvocationByEventPage;
